import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { request } from './api';

export function useRemote<T>(path: string | null, schema: z.ZodType<T>) {
  const [state, setState] = useState<{
    path: string | null;
    data: T | null;
    error: Error | null;
    loading: boolean;
  }>({ path, data: null, error: null, loading: !!path });
  const [revision, setRevision] = useState(0);
  const activeRequest = useRef<AbortController | null>(null);
  useEffect(() => {
    if (!path) return;
    const controller = new AbortController();
    activeRequest.current = controller;
    setState((previous) => ({
      path,
      data: previous.path === path ? previous.data : null,
      error: null,
      loading: true,
    }));
    void request(path, schema, { signal: controller.signal })
      .then((data) => {
        if (!controller.signal.aborted)
          setState({ path, data, error: null, loading: false });
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted)
          setState({
            path,
            data: null,
            error: error instanceof Error ? error : new Error('REQUEST_FAILED'),
            loading: false,
          });
      });
    return () => controller.abort();
  }, [path, schema, revision]);
  const reload = useCallback(() => setRevision((value) => value + 1), []);
  const invalidate = useCallback(
    (error: Error) => {
      activeRequest.current?.abort();
      setState({ path, data: null, error, loading: false });
    },
    [path]
  );
  return {
    ...(state.path === path
      ? state
      : { path, data: null, error: null, loading: !!path }),
    reload,
    invalidate,
  };
}
